/**
 * Client wrappers for ClinicalHUD3 Live, Teaching, and case-bank callables.
 */

import firebase from "firebase/compat/app";

export async function recordClinicalHud3LiveAttestation() {
  const callable = firebase
    .functions()
    .httpsCallable("recordClinicalHud3LiveAttestation");
  const result = await callable({});
  return result?.data || null;
}

export async function logClinicalHud3AdminLiveEntry() {
  const callable = firebase
    .functions()
    .httpsCallable("logClinicalHud3AdminLiveEntry");
  const result = await callable({});
  return result?.data || null;
}

/**
 * @param {{ usPracticeAttested: boolean, npi?: string }} input
 */
export async function saveLearnerProfile(input) {
  const callable = firebase
    .functions()
    .httpsCallable("saveClinicalHud3LearnerProfile");
  const result = await callable(input);
  return result?.data || null;
}

/**
 * Fire-and-forget usage event. Failures are logged; never throws into the UI.
 * @param {string} event
 * @param {string} [caseId]
 */
export async function logUsageEvent(event, caseId) {
  try {
    const callable = firebase
      .functions()
      .httpsCallable("logClinicalHud3UsageEvent");
    const payload =
      caseId == null || caseId === ""
        ? { event }
        : { event, caseId };
    await callable(payload);
  } catch (err) {
    console.error("[clinicalHud3] logUsageEvent failed", err);
  }
}

/**
 * @param {{ topic: string }} input
 * @returns {Promise<{ caseId: string }>}
 */
export async function draftClinicalHud3Case(input) {
  const callable = firebase.functions().httpsCallable("draftClinicalHud3Case");
  const result = await callable(input);
  return result?.data || null;
}

/**
 * @param {{
 *   caseId: string,
 *   title: string,
 *   transcript: string,
 *   diagnoses: Array<{ name: string, mustNotMiss: boolean }>,
 *   teachingPoints: string[],
 * }} input
 */
export async function updateClinicalHud3CaseDraft(input) {
  const callable = firebase
    .functions()
    .httpsCallable("updateClinicalHud3CaseDraft");
  const result = await callable(input);
  return result?.data || null;
}

/**
 * @param {{ caseId: string, status: 'approved' | 'retired' }} input
 */
export async function setClinicalHud3CaseStatus(input) {
  const callable = firebase
    .functions()
    .httpsCallable("setClinicalHud3CaseStatus");
  const result = await callable(input);
  return result?.data || null;
}

/**
 * @param {{ sessionId: string, entries: string[] }} input
 */
/**
 * Teaching pearls. scope "actual" uses the case's actual diagnosis.
 * scope "alternatives" uses the differential titles except that diagnosis.
 * @param {{
 *   sessionId: string,
 *   scope: "actual" | "alternatives",
 *   differentialTitles?: string[],
 * }} input
 * @returns {Promise<{ pearls: string[], scope: string, actualDiagnosis: string }>}
 */
export async function generateClinicalHud3Pearls(input) {
  const callable = firebase
    .functions()
    .httpsCallable("generateClinicalHud3Pearls");
  const result = await callable(input);
  return result?.data || null;
}

export async function scoreClinicalHud3Ddx(input) {
  const callable = firebase.functions().httpsCallable("scoreClinicalHud3Ddx");
  const result = await callable(input);
  return result?.data || null;
}

/**
 * @param {{ from: string | Date, to: string | Date, usAttestedOnly?: boolean }} input
 */
export async function getClinicalHud3UsageSummary(input) {
  const callable = firebase
    .functions()
    .httpsCallable("getClinicalHud3UsageSummary");
  const result = await callable(input);
  return result?.data || null;
}

/**
 * Build and download a CSV of per-month usage summary rows.
 * @param {Array<object>} perMonth
 * @param {string} [filename]
 */
export function downloadClinicalHud3UsageCsv(
  perMonth,
  filename = "clinicalhud3-usage.csv",
) {
  const rows = Array.isArray(perMonth) ? perMonth : [];
  const header = [
    "month",
    "uniqueUsers",
    "teachingSessionsStarted",
    "liveSessionsStarted",
    "ddxCommits",
    "casesCompleted",
    "eventCount",
  ];
  const lines = [header.join(",")];
  rows.forEach((row) => {
    lines.push(
      [
        row.month || "",
        row.uniqueUsers ?? "",
        row.teachingSessionsStarted ?? "",
        row.liveSessionsStarted ?? "",
        row.ddxCommits ?? "",
        row.casesCompleted ?? "",
        row.eventCount ?? "",
      ].join(","),
    );
  });
  const blob = new Blob([lines.join("\n")], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
